import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import { FaBars, FaSun, FaMoon, FaBell, FaCog, FaUserEdit, FaBan } from "react-icons/fa";
import "./Navbar.css";
import SearchUsers from "./SearchUsers";
import ProfileImage from "./ProfileImage";
import NotificationsDropdown from "./Notifications/NotificationsDropdown";
import { authFetch } from "../Utilities/Api";
import io from 'socket.io-client';

// ⚠️ Ensure this matches your production URL
const ENDPOINT = "https://talkear.onrender.com";

var socket;

const Navbar = () => {
    const { user, logout } = useContext(AuthContext);

    // Initialise theme from localStorage
    const [darkMode, setDarkMode] = useState(() => {
        try {
            const saved = localStorage.getItem("theme");
            if (saved === "light" || saved === "dark") {
                return saved === "dark";
            }
            return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
        } catch {
            return false;
        }
    });

    const [menuOpen, setMenuOpen] = useState(false);
    const [showNotifs, setShowNotifs] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const notifRef = useRef(null);
    const settingsRef = useRef(null);
    const navigate = useNavigate();

    // --------------------------------------------------------
    // 🔔 REAL-TIME NOTIFICATIONS & SOCKET SETUP
    // --------------------------------------------------------
    useEffect(() => {
        if (!user) return;

        // A. Fetch Initial Unread Count
        const fetchUnreadCount = async () => {
            try {
                const res = await authFetch('/notifications');
                const data = await res.json();
                if (res.ok) {
                    setUnreadCount(data.unreadCount || 0);
                }
            } catch (err) {
                console.error("❌ [Navbar] Failed to fetch notification count", err);
            }
        };
        fetchUnreadCount();

        // B. Connect to Socket
        socket = io(ENDPOINT, { transports: ['websocket'] });

        socket.on("connect", () => {
            socket.emit("setup", user);
        });

        // C. Listen for Real-Time Notification Alerts
        socket.on("new_notification", (notif) => {
            setUnreadCount((prev) => prev + 1);
        });

        // ✅ NEW: Force refresh count (Triggered by friendController.js)
        socket.on("notification_count_update", () => {
            fetchUnreadCount();
        });

        // ✅ NEW: Listen for changes in the friendship state
        socket.on("friendListUpdated", () => {
            console.log("👥 [Socket] Friend list changed");
        });

        socket.on("connect_error", (err) => {
            console.error("⚠️ [Socket] Connection Error:", err.message);
        });

        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, [user]);

    // Keep DOM + localStorage in sync for Theme
    useEffect(() => {
        const theme = darkMode ? "dark" : "light";
        try {
            localStorage.setItem("theme", theme);
        } catch (err) { }

        document.documentElement.setAttribute("data-theme", theme);
        document.documentElement.classList.toggle("dark", darkMode);
    }, [darkMode]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notifRef.current && !notifRef.current.contains(event.target)) {
                setShowNotifs(false);
            }
            if (settingsRef.current && !settingsRef.current.contains(event.target)) {
                setShowSettings(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleToggleMenu = () => setMenuOpen((prev) => !prev);

    const handleLogout = async () => {
        await logout();
        setMenuOpen(false);
        setShowSettings(false);
        setTimeout(() => navigate("/"), 50);
    };

    const toggleTheme = () => setDarkMode((prev) => !prev);

    const closeMenu = () => {
        setMenuOpen(false);
        setShowSettings(false);
        setShowNotifs(false);
    };

    // Helper for dropdown items
    const DropdownItem = ({ to, icon, label, onClick }) => {
        if (onClick) {
            return (
                <button onClick={onClick} className="dropdown-item-btn">
                    <span className="dropdown-icon">{icon}</span>
                    {label}
                </button>
            );
        }
        return (
            <Link to={to} className="dropdown-item-link" onClick={() => setShowSettings(false)}>
                <span className="dropdown-icon">{icon}</span>
                {label}
            </Link>
        );
    };

    const MobileLink = ({ href, children }) => (
        <a
            href={href}
            className="menu-item"
            onClick={(e) => {
                e.preventDefault();
                closeMenu();
                navigate(href);
            }}
            style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
        >
            {children}
        </a>
    );

    return (
        <nav className="navbar-container">
            <div className="navbar-inner">
                {/* Logo Link */}
                <Link
                    to={user ? "/profile" : "/"}
                    className="navbar-logo"
                    onClick={() => closeMenu()}
                >
                    talkear
                </Link>

                {user && (
                    <div className="navbar-search">
                        <SearchUsers />
                    </div>
                )}

                <div className="navbar-controls">
                    {/* Theme Toggle */}
                    <button
                        className="theme-toggle"
                        onClick={toggleTheme}
                        title="Toggle theme"
                        type="button"
                    >
                        {darkMode ? <FaSun /> : <FaMoon />}
                    </button>

                    {user && (
                        <>
                            {/* Notifications */}
                            <div className="notif-wrapper" style={{ position: 'relative' }} ref={notifRef}>
                                <button
                                    className={`navbar-icon-btn ${showNotifs ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowNotifs(!showNotifs);
                                        setShowSettings(false);
                                    }}
                                    title="Notifications"
                                    type="button"
                                    style={{ position: 'relative' }}
                                >
                                    <FaBell />
                                    {unreadCount > 0 && (
                                        <span className="notif-badge">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    )}
                                </button>
                                {showNotifs && (
                                    <NotificationsDropdown onClose={() => setShowNotifs(false)} />
                                )}
                            </div>

                            {/* Settings Dropdown */}
                            <div className="settings-wrapper" style={{ position: 'relative' }} ref={settingsRef}>
                                <button
                                    className={`navbar-icon-btn ${showSettings ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowSettings(!showSettings);
                                        setShowNotifs(false);
                                    }}
                                    title="Settings"
                                    type="button"
                                >
                                    <FaCog />
                                </button>

                                {showSettings && (
                                    <div className="settings-dropdown">
                                        <div className="dropdown-header">
                                            <strong>Account Settings</strong>
                                        </div>
                                        <DropdownItem to="/settings" icon={<FaCog />} label="General & Privacy" />
                                        <DropdownItem to="/settings" icon={<FaUserEdit />} label="Edit Profile" />
                                        <DropdownItem to="/settings" icon={<FaBan />} label="Blocked Users" />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Burger Menu Button */}
                    <button
                        className="navbar-toggle"
                        onClick={handleToggleMenu}
                        title="Menu"
                        type="button"
                        style={{ zIndex: 1001 }}
                    >
                        <FaBars />
                    </button>
                </div>

                {/* Mobile/Burger Menu Overlay */}
                {menuOpen && (
                    <div className="navbar-menu" style={{ display: 'flex', flexDirection: 'column', zIndex: 1000 }}>
                        {user ? (
                            <>
                                <div className="navbar-user">
                                    <ProfileImage src={user.profilePicture} className="navbar-avatar" />
                                    <span className="navbar-username">{user.nickname || user.username}</span>
                                </div>

                                <MobileLink href="/profile">Profile</MobileLink>
                                <MobileLink href="/requests">Requests</MobileLink>
                                <MobileLink href="/friends">Friends</MobileLink>
                                <MobileLink href="/calendar">Calendar</MobileLink>

                                {/* ✅ Dynamic Admin/Mod Navigation */}
                                {["moderator", "admin", "owner"].includes(user.role) && (
                                    <MobileLink href="/moderation">Moderation Dashboard</MobileLink>
                                )}

                                {["admin", "owner"].includes(user.role) && (
                                    <MobileLink href="/admin">Manage Users</MobileLink>
                                )}

                                <button onClick={handleLogout} className="logout-btn">
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <MobileLink href="/">Login</MobileLink>
                                <MobileLink href="/register">Register</MobileLink>
                            </>
                        )}
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;